import type { TemplateVariant } from './adminTypes';

/** Viz modes exposed on the landing showcase 3D viewport.
 *
 *  `velocity` is the band-integrated particle-velocity field. Unlike
 *  `volumetric` it has NO frequency axis — the band is integrated away
 *  server-side — so `modalFreq` / `interactiveFreq` mean nothing to a slice
 *  that selects it. Its scrub axis is speed (`view.velocityLevel`). */
export type LandingShowcaseVizMode = 'volumetric' | 'reflections' | 'velocity';

export interface LandingShowcaseText {
  eyebrow?: string;
  title: string;
  body?: string;
}

export interface LandingShowcaseSceneConfig {
  /** Which precomputed snapshot to load (`bare` strips panels). */
  variant: TemplateVariant;
  /** Which before/after viz slot to keep and paint — no visitor toggle.
   *
   *  The showcase wipe pins this to `before`: its field belongs on the
   *  UNTREATED half (see UpsellRoom's RoomScene), so the treated slot would be
   *  megabytes nothing ever paints. */
  compare: 'before' | 'after';
  vizMode: LandingShowcaseVizMode;
  /** Fixed Hz when `interactiveFreq` is false. */
  modalFreq?: number;
  interactiveFreq?: boolean;
  interactiveVizMode?: boolean;
  /** Modes the visitor can pick when `interactiveVizMode` is true. Omitted in JSON
   *  → both modes. After parse, always includes `vizMode` even if omitted from
   *  the authored list. */
  allowedVizModes: LandingShowcaseVizMode[];
  orbit?: boolean;
}

export interface LandingShowcaseSlice {
  text: LandingShowcaseText;
  scene: LandingShowcaseSceneConfig;
  charts: string[];
  /** Optional line under the Charts column header on landing. */
  chartsCaption?: string;
}

export interface LandingShowcaseTemplateEntry {
  templateId: string;
  label?: string;
  blurb?: string;
  slice: LandingShowcaseSlice;
}

export interface LandingShowcaseRegistry {
  cycleIntervalMs: number;
  autoCycleTemplates: boolean;
  templates: LandingShowcaseTemplateEntry[];
}

/** Every mode a landing slice may select. Exported because web's ShowcaseRoom
 *  has to decide whether the editor's CURRENT vizMode is one this screen owns
 *  before it corrects it, and a hand-copied list there is exactly the kind of
 *  id-map drift this repo keeps getting bitten by. */
export const LANDING_SHOWCASE_VIZ_MODES: readonly LandingShowcaseVizMode[] =
  ['volumetric', 'reflections', 'velocity'];

const VIZ_MODES: LandingShowcaseVizMode[] = [...LANDING_SHOWCASE_VIZ_MODES];

function resolveAllowedVizModes(
  vizMode: LandingShowcaseVizMode,
  explicit?: LandingShowcaseVizMode[],
): LandingShowcaseVizMode[] {
  const base = explicit ?? [...VIZ_MODES];
  if (base.includes(vizMode)) return base;
  // Initial vizMode must always be renderable; editor mistakes (vizMode outside
  // allowedVizModes) are normalized rather than taking down /landing.
  return [vizMode, ...base];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function parseText(raw: unknown, path: string): LandingShowcaseText {
  if (!isRecord(raw)) throw new Error(`${path}: expected object`);
  const title = raw.title;
  if (typeof title !== 'string' || !title.trim()) throw new Error(`${path}.title: required string`);
  const eyebrow = raw.eyebrow;
  const body = raw.body;
  if (eyebrow != null && typeof eyebrow !== 'string') throw new Error(`${path}.eyebrow: string`);
  if (body != null && typeof body !== 'string') throw new Error(`${path}.body: string`);
  return {
    title,
    ...(eyebrow != null ? { eyebrow } : {}),
    ...(body != null ? { body } : {}),
  };
}

function parseVizMode(raw: unknown, path: string): LandingShowcaseVizMode {
  if (raw !== 'volumetric' && raw !== 'reflections' && raw !== 'velocity') {
    throw new Error(`${path}: expected volumetric | reflections | velocity`);
  }
  return raw;
}

function parseScene(raw: unknown, path: string): LandingShowcaseSceneConfig {
  if (!isRecord(raw)) throw new Error(`${path}: expected object`);
  const variant = raw.variant;
  if (variant !== 'bare' && variant !== 'treated') {
    throw new Error(`${path}.variant: expected bare | treated`);
  }
  const compare = raw.compare;
  if (compare !== 'before' && compare !== 'after') {
    throw new Error(`${path}.compare: expected before | after`);
  }
  const vizMode = parseVizMode(raw.vizMode, `${path}.vizMode`);
  const interactiveFreq = raw.interactiveFreq;
  const interactiveVizMode = raw.interactiveVizMode;
  const orbit = raw.orbit;
  const modalFreq = raw.modalFreq;
  if (interactiveFreq != null && typeof interactiveFreq !== 'boolean') {
    throw new Error(`${path}.interactiveFreq: boolean`);
  }
  if (interactiveVizMode != null && typeof interactiveVizMode !== 'boolean') {
    throw new Error(`${path}.interactiveVizMode: boolean`);
  }
  if (orbit != null && typeof orbit !== 'boolean') {
    throw new Error(`${path}.orbit: boolean`);
  }
  if (modalFreq != null && (typeof modalFreq !== 'number' || !Number.isFinite(modalFreq))) {
    throw new Error(`${path}.modalFreq: finite number`);
  }
  const freqInteractive = interactiveFreq ?? false;
  if (!freqInteractive && modalFreq == null) {
    throw new Error(`${path}.modalFreq: required when interactiveFreq is false`);
  }

  let explicitAllowed: LandingShowcaseVizMode[] | undefined;
  if (raw.allowedVizModes != null) {
    if (!Array.isArray(raw.allowedVizModes) || raw.allowedVizModes.length === 0) {
      throw new Error(`${path}.allowedVizModes: non-empty array`);
    }
    explicitAllowed = raw.allowedVizModes.map((m, i) =>
      parseVizMode(m, `${path}.allowedVizModes[${i}]`),
    );
  }
  const allowedVizModes = resolveAllowedVizModes(vizMode, explicitAllowed);

  return {
    variant,
    compare,
    vizMode,
    allowedVizModes,
    ...(modalFreq != null ? { modalFreq } : {}),
    ...(interactiveFreq != null ? { interactiveFreq } : {}),
    ...(interactiveVizMode != null ? { interactiveVizMode } : {}),
    ...(orbit != null ? { orbit } : {}),
  };
}

function parseSlice(raw: unknown, path: string): LandingShowcaseSlice {
  if (!isRecord(raw)) throw new Error(`${path}: expected object`);
  const charts = raw.charts;
  if (!Array.isArray(charts) || charts.length === 0) {
    throw new Error(`${path}.charts: non-empty string array`);
  }
  if (!charts.every((c) => typeof c === 'string' && c.length > 0)) {
    throw new Error(`${path}.charts: chart ids must be strings`);
  }
  const chartsCaption = raw.chartsCaption;
  if (chartsCaption != null && typeof chartsCaption !== 'string') {
    throw new Error(`${path}.chartsCaption: string`);
  }
  return {
    text: parseText(raw.text, `${path}.text`),
    scene: parseScene(raw.scene, `${path}.scene`),
    charts: charts as string[],
    ...(chartsCaption != null && chartsCaption.length > 0 ? { chartsCaption } : {}),
  };
}

/** Parse and validate a landing showcase registry object. */
export function parseLandingShowcaseRegistry(raw: unknown): LandingShowcaseRegistry {
  if (!isRecord(raw)) throw new Error('registry: expected object');
  const cycleIntervalMs = raw.cycleIntervalMs;
  if (typeof cycleIntervalMs !== 'number' || !Number.isFinite(cycleIntervalMs) || cycleIntervalMs <= 0) {
    throw new Error('cycleIntervalMs: positive number');
  }
  const autoCycleTemplates = raw.autoCycleTemplates;
  if (typeof autoCycleTemplates !== 'boolean') {
    throw new Error('autoCycleTemplates: boolean');
  }
  const templates = raw.templates;
  if (!Array.isArray(templates) || templates.length === 0) {
    throw new Error('templates: non-empty array');
  }
  const seen = new Set<string>();
  const parsed: LandingShowcaseTemplateEntry[] = templates.map((t, i) => {
    if (!isRecord(t)) throw new Error(`templates[${i}]: expected object`);
    const templateId = t.templateId;
    if (typeof templateId !== 'string' || !templateId.trim()) {
      throw new Error(`templates[${i}].templateId: required string`);
    }
    if (seen.has(templateId)) throw new Error(`templates[${i}]: duplicate templateId ${templateId}`);
    seen.add(templateId);
    const label = t.label;
    const blurb = t.blurb;
    if (label != null && typeof label !== 'string') throw new Error(`templates[${i}].label: string`);
    if (blurb != null && typeof blurb !== 'string') throw new Error(`templates[${i}].blurb: string`);
    return {
      templateId,
      ...(label != null ? { label } : {}),
      ...(blurb != null ? { blurb } : {}),
      slice: parseSlice(t.slice, `templates[${i}].slice`),
    };
  });
  return { cycleIntervalMs, autoCycleTemplates, templates: parsed };
}

export type ParseLandingShowcaseResult =
  | { ok: true; registry: LandingShowcaseRegistry }
  | { ok: false; error: string };

/** Parse without throwing — for dev-time registry edits and UI fallbacks. */
export function tryParseLandingShowcaseRegistry(raw: unknown): ParseLandingShowcaseResult {
  try {
    return { ok: true, registry: parseLandingShowcaseRegistry(raw) };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return { ok: false, error: message };
  }
}

export function defaultAllowedVizModes(
  scene: LandingShowcaseSceneConfig,
): LandingShowcaseVizMode[] {
  return scene.allowedVizModes;
}

export function showcaseModalFreq(scene: LandingShowcaseSceneConfig): number {
  return scene.modalFreq ?? 80;
}
